/**
 * VÉRAULT · Client Itinerary — Live Day Banner (Wave 12.i)
 * ─────────────────────────────────────────────────────────────────
 * A calm, auto-rotating carousel that surfaces, in this priority:
 *   1. Day-level advisor "priority_note" (always pinned to slide 0).
 *   2. Item-level "notes_important" notes from items on TODAY's day.
 *   3. The current / next event ("Now: …" / "Up next: …") computed
 *      from the destination-local clock + each item's `time` field.
 *
 * Auto-rotates every 7 seconds. The client can also tap left / right
 * or the dots to step through manually. The component is silent
 * (renders nothing) if there's no current day / no banner content.
 *
 * Live-tracking
 * ─────────────
 * Every minute we re-compute the "current event" from the day's
 * three blocks (morning / afternoon / evening) by comparing local
 * time to each item's `time`. The slide updates seamlessly.
 *
 * Props:
 *   days: client-portal itinerary day list (real days only)
 *   timezone: IANA tz for the trip's current destination
 */
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, AlertCircle, MapPin } from "lucide-react";
import { resolveTransportDisplay } from "../../../advisor/journey/transportation";

const SLIDE_INTERVAL_MS = 7000;

function parseLocalTime(timeStr) {
  // "9:30 PM" → minutes from midnight, or null.
  if (!timeStr) return null;
  const m = String(timeStr).trim().match(/(\d{1,2}):(\d{2})\s*(am|pm|AM|PM)?/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const ampm = (m[3] || "").toLowerCase();
  if (ampm === "pm" && h < 12) h += 12;
  if (ampm === "am" && h === 12) h = 0;
  return h * 60 + mm;
}

function nowMinsInTz(tz) {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hour: "numeric", minute: "numeric", hour12: false,
    });
    const parts = fmt.formatToParts(new Date());
    const h = parseInt(parts.find((p) => p.type === "hour").value, 10);
    const m = parseInt(parts.find((p) => p.type === "minute").value, 10);
    return h * 60 + m;
  } catch {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  }
}

function todayDateStringInTz(tz) {
  try {
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
    });
    return fmt.format(new Date()); // YYYY-MM-DD
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function collectItemsForDay(day) {
  const out = [];
  ["morning", "afternoon", "evening", "late_night"].forEach((bk) => {
    const b = day?.[bk] || {};
    ["primary_activity", "secondary_activity"].forEach((sk) => {
      const it = b[sk];
      if (it && it.name) out.push({ block: bk, ...it });
    });
    (b.extra_items || []).forEach((it) => {
      if (it && it.name) out.push({ block: bk, ...it });
    });
  });
  // Sort by time ascending; un-timed items drift to the end.
  out.sort((a, b) => {
    const at = parseLocalTime(a.time);
    const bt = parseLocalTime(b.time);
    if (at == null && bt == null) return 0;
    if (at == null) return 1;
    if (bt == null) return -1;
    return at - bt;
  });
  return out;
}

export function LiveDayBanner({ days, timezone, bookings, travelPrepReminders }) {
  const tz = timezone || "UTC";
  const [tick, setTick] = useState(0);

  // Tick every 60s so live "Now / Next" computation refreshes.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const today = useMemo(() => todayDateStringInTz(tz), [tz, tick]);  // eslint-disable-line react-hooks/exhaustive-deps
  const currentDay = useMemo(() => {
    if (!days || days.length === 0) return null;
    return (
      days.find((d) => (d.date || "").slice(0, 10) === today) ||
      // If today isn't in the itinerary, fall back to the soonest
      // future day so the banner stays useful pre-trip.
      days.find((d) => (d.date || "").slice(0, 10) > today) ||
      null
    );
  }, [days, today]);

  const items = useMemo(() => (currentDay ? collectItemsForDay(currentDay) : []), [currentDay]);

  const nowMin = nowMinsInTz(tz);  // recomputed each tick via re-render
  const { current, next } = useMemo(() => {
    if ((currentDay?.date || "").slice(0, 10) !== today) {
      // Pre-trip preview: surface the first item as "First chapter".
      return { current: null, next: items[0] || null };
    }
    let cur = null;
    let nxt = null;
    for (const it of items) {
      const t = parseLocalTime(it.time);
      if (t == null) continue;
      if (t <= nowMin) cur = it; else { nxt = it; break; }
    }
    return { current: cur, next: nxt };
  }, [items, nowMin, currentDay, today]);

  // Compose the slide list.
  const slides = useMemo(() => {
    const out = [];
    // 1. Day priority note (always slide 0)
    const priority = (currentDay?.priority_note || "").trim();
    if (priority) {
      out.push({
        kind: "priority",
        eyebrow: "From your advisor",
        body: priority,
        icon: AlertCircle,
      });
    }
    // 2. Live "Now / Next"
    if (current) {
      out.push({
        kind: "now",
        eyebrow: "Now",
        body: `${current.name}${current.time ? ` · ${current.time}` : ""}`,
        sub: current.location || null,
        icon: MapPin,
      });
    }
    if (next) {
      out.push({
        kind: "next",
        eyebrow: current ? "Up next" : "Coming up",
        body: `${next.name}${next.time ? ` · ${next.time}` : ""}`,
        sub: next.location || null,
        icon: Sparkles,
      });
    }
    // 3. Important item notes
    for (const it of items) {
      if (it.notes_important && (it.notes || "").trim()) {
        out.push({
          kind: "note",
          eyebrow: `Note · ${it.name}`,
          body: (it.notes || "").trim(),
          sub: it.time || null,
          icon: AlertCircle,
        });
      }
      // Wave 12.z — surface the new `important_details` field
      // (scannable bullets) as one banner card per item, regardless
      // of whether `notes_important` is set. Each card shows up to
      // 3 lines so the banner stays compact.
      const importantLines = (it.important_details || "")
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (importantLines.length > 0) {
        out.push({
          kind: "important",
          eyebrow: `Important · ${it.name}`,
          body: importantLines.slice(0, 3).join(" · "),
          sub: it.time || null,
          icon: AlertCircle,
        });
      }
      // Wave 12.ab — Transportation in the daily banner. Surfaces
      // ready-time + readiness language only; the resolver strips
      // destinations for surprise events and Limited visibility.
      if (it.transportation) {
        const isSurprise = Boolean(it.is_surprise || it.surprise || it.surprise_until);
        const t = resolveTransportDisplay(it.transportation, { isSurprise, isAdvisorView: false, itemName: it.name });
        if (t.allowed) {
          out.push({
            kind: "transport",
            // Eyebrow stays generic for surprise/limited cases (never name the event).
            eyebrow: (isSurprise || (it.transportation?.visibility || "full") === "limited")
              ? t.eyebrow
              : `${t.eyebrow} · ${it.name}`,
            body: t.body,
            sub: t.sub,
            icon: t.icon,
          });
        }
      }
    }
    // 4. Wave 12.o — Important Travel Ops notes from bookings whose
    // date matches today (stays via start/end overlap; flights/transfers
    // via depart_at / pickup_at / start_date).
    if (Array.isArray(bookings) && bookings.length > 0) {
      const dayDate = (currentDay?.date || today).slice(0, 10);
      const inWindow = (b) => {
        const t = (b.booking_type || "").toLowerCase();
        if (t === "hotel") {
          const s = (b.start_date || "").slice(0, 10);
          const e = (b.end_date || "").slice(0, 10);
          return s && e && dayDate >= s && dayDate <= e;
        }
        const dt = (b.depart_at || b.pickup_at || b.start_date || "").slice(0, 10);
        return dt === dayDate;
      };
      for (const b of bookings) {
        if (!b.travel_ops_important) continue;
        if (!(b.travel_ops_notes || "").trim()) continue;
        if (!inWindow(b)) continue;
        const label = b.vendor || b.supplier || b.airline || b.booking_type || "Travel";
        out.push({
          kind: "ops",
          eyebrow: `Travel · ${label}`,
          body: (b.travel_ops_notes || "").trim(),
          sub: b.confirmation_number || null,
          icon: AlertCircle,
        });
      }
    }
    // 5. Wave 32 · Phase 2 — Featured Reminders from Travel Prep pages.
    if (Array.isArray(travelPrepReminders) && travelPrepReminders.length > 0) {
      for (const r of travelPrepReminders) {
        const text = (r.text || r.section_title || "").trim();
        if (!text) continue;
        out.push({
          kind: "prep-reminder",
          eyebrow: r.page_title ? `Prep · ${r.page_title}` : "Travel Prep",
          body: text,
          sub: null,
          icon: Sparkles,
        });
      }
    }
    return out;
  }, [currentDay, current, next, items, bookings, today, travelPrepReminders]);

  const [idx, setIdx] = useState(0);
  // Reset to slide 0 whenever the slide set changes.
  useEffect(() => { setIdx(0); }, [slides.length]);
  // Auto-rotate.
  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const t = setInterval(() => setIdx((n) => (n + 1) % slides.length), SLIDE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [slides.length]);

  if (slides.length === 0) return null;
  const slide = slides[idx];
  const Icon = slide.icon || Sparkles;

  return (
    <div
      className="relative overflow-hidden rounded-md border border-[hsl(var(--accent-gold))]/30 bg-gradient-to-br from-[hsl(28,18%,16%)] via-[hsl(20,12%,11%)] to-[hsl(20,12%,9%)] text-white shadow-sm"
      data-testid="live-day-banner"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(212,175,55,0.18)_0%,_transparent_60%)]" />
      <div className="relative px-5 sm:px-8 py-5 sm:py-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${slide.kind}-${idx}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.35 }}
            className="min-h-[68px]"
            data-testid={`live-banner-slide-${idx}`}
          >
            <div className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.24em] opacity-80">
              <Icon className="size-3" strokeWidth={1.5} />
              {slide.eyebrow}
            </div>
            <p className="mt-1.5 font-serif text-[18px] sm:text-[20px] leading-snug tracking-tight max-w-2xl">
              {slide.body}
            </p>
            {slide.sub && (
              <p className="mt-1 font-accent italic text-[12.5px] opacity-70">{slide.sub}</p>
            )}
          </motion.div>
        </AnimatePresence>

        {slides.length > 1 && (
          <div className="absolute right-4 sm:right-7 bottom-3 flex items-center gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                className={`h-1 rounded-full transition-all ${i === idx ? "w-5 bg-[hsl(var(--accent-gold))]" : "w-1.5 bg-white/30 hover:bg-white/50"}`}
                aria-label={`Slide ${i + 1}`}
                data-testid={`live-banner-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default LiveDayBanner;
